import { useMemoizedFn } from "ahooks";
import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";

import { useEffect, useMemo, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import FlightLandOutlinedIcon from "@mui/icons-material/FlightLandOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { LoadingButton } from "@mui/lab";
import { Grid, IconButton, InputAdornment, TextField } from "@mui/material";

import Programs from "@/components/AlertModal/Programs";
import SearchAirport from "@/components/SearchBar/SearchAirport";
import SearchDate from "@/components/SearchBar/SearchDate";
import { AIRLINE_NAME_MAP } from "@/constants/airline";
import { AirportMapItem } from "@/constants/airport";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";

export interface SearchConditionType {
  fromLocation: AirportMapItem[];
  toLocation: AirportMapItem[];
  oneWayDate?: number;
  airline: { label: string; value: string }[];
  flightNumber?: string;
  cabinClass: string;
}

const defaultValue = {
  fromLocation: [],
  toLocation: [],
  airline: [],
  flightNumber: "",
  cabinClass: "Economy",
} as SearchConditionType;

const SearchBar = (props: {
  loading: boolean;
  onSearch: (condition: SearchConditionType) => void;
}) => {
  const { loading, onSearch } = props;
  // const [condition, setCondition] = useLocalStorageState<SearchConditionType>(
  //   "LiveSeatMap_searchCondition",
  //   defaultValue
  // ) as [SearchConditionType, (value: SearchConditionType) => void];

  const [condition, setCondition] = useState<SearchConditionType>(defaultValue);

  const { enqueueSnackbar } = useSnackbar();

  const { isMobile } = usePlatform();

  const onRoundTripDepartureDateChange = useEvent((date: Dayjs | null) => {
    if (condition) {
      setCondition({
        ...condition,
        oneWayDate: date ? dayjs(date).valueOf() : 0,
      });
    }
  });

  const operatorProgramsOptions = useMemo(() => {
    return Object.keys(AIRLINE_NAME_MAP)
      .map((key) => ({
        value: key,
        label: AIRLINE_NAME_MAP[key],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const validateCondition = useMemoizedFn((condition: SearchConditionType) => {
    let errMsg = "";

    if (!condition?.fromLocation?.length) {
      errMsg = "Please select departure airport";
    }

    if (!condition?.toLocation?.length) {
      errMsg = "Please select departure airport";
    }

    if (!condition?.airline?.length) {
      errMsg = "Please select airline";
    }

    if (!condition?.oneWayDate) {
      errMsg = "Please select departure date";
    }

    if (!condition?.flightNumber) {
      errMsg = "Please input flight number";
    }

    return errMsg;
  });

  const handleSearch = useMemoizedFn(() => {
    const errMsg = validateCondition(condition);
    if (errMsg) {
      enqueueSnackbar(errMsg, {
        variant: "info",
        anchorOrigin: {
          vertical: isMobile ? "bottom" : "top",
          horizontal: "center",
        },
        autoHideDuration: 2000,
      });
      return;
    }

    onSearch(condition);
  });

  useEffect(() => {
    const errMsg = validateCondition(condition);
    if (!errMsg) {
      onSearch(condition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container rowSpacing={2} columnSpacing={1} className="relative">
      <Grid item xs={12}>
        <Grid container alignItems="center" rowSpacing={2}>
          <Grid item xs={12} sm={12}>
            <SearchAirport
              placeholder="Where from?"
              Icon={<FlightTakeoffOutlinedIcon />}
              selectData={condition?.fromLocation}
              onChange={(data) => {
                setCondition({ ...condition, fromLocation: data });
              }}
              maxCount={1}
              showTip={false}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <SearchAirport
              placeholder="Where to?"
              Icon={<FlightLandOutlinedIcon />}
              selectData={condition?.toLocation}
              onChange={(data) => {
                setCondition({ ...condition, toLocation: data });
              }}
              maxCount={1}
              showTip={false}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <SearchDate
              key="departureDate"
              label="Departure Date"
              defaultValue={condition?.oneWayDate ? dayjs(condition.oneWayDate) : undefined}
              onChange={onRoundTripDepartureDateChange}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <Programs
              title="Excluded Airline(s)"
              options={operatorProgramsOptions}
              selectOptions={condition?.airline}
              onChange={(v) => {
                setCondition({
                  ...condition,
                  airline: v.slice(-1),
                });
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              variant="outlined"
              fullWidth
              value={condition?.flightNumber}
              placeholder="Flight number"
              onChange={(e) => {
                setCondition({ ...condition, flightNumber: e.target.value });
              }}
              InputProps={{
                inputProps: { min: 0 },
                endAdornment: condition?.flightNumber && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setCondition({ ...condition, flightNumber: "" });
                      }}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <LoadingButton
              variant="contained"
              className="w-full"
              size="large"
              loading={loading}
              onClick={handleSearch}
              startIcon={<SearchIcon />}
            >
              Search
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default SearchBar;
